import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { ClockIcon, MapPinIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { getTournamentEvents, getTournaments } from '../../lib/supabase';
import type { DayEvents } from '../../types/content';


export default function MobileSchedule() {
  const [dayEvents, setDayEvents] = useState<DayEvents[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data: tournaments } = await getTournaments();
      const upcoming = tournaments?.find((t: { status: string }) => t.status === 'upcoming')
        || [...(tournaments || [])].sort((a, b) => b.start_date?.localeCompare(a.start_date))[0];
      if (upcoming?.id) {
        const { data } = await getTournamentEvents(upcoming.id);
        if (data && data.length > 0) {
          const grouped: Record<string, DayEvents['events']> = {};
          for (const ev of data) {
            const d = ev.date || ev.start_date?.split('T')[0] || '';
            if (!grouped[d]) grouped[d] = [];
            grouped[d].push(ev);
          }
          const sorted = Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, events]) => ({ date, events }));
          setDayEvents(sorted);
        } else {
          setDayEvents([]);
        }
      } else {
        setDayEvents([]);
      }
    } catch {
      setDayEvents([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="px-4 pt-2 pb-8">
      {dayEvents.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <CalendarDaysIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No schedule available yet.</p>
        </div>
      )}

      {dayEvents.map((day) => {
        let dateLabel = '';
        try {
          dateLabel = format(parseISO(day.date), 'EEEE, MMMM d');
        } catch {
          dateLabel = day.date;
        }

        return (
          <div key={day.date} className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-golf-orange flex flex-col items-center justify-center text-white flex-shrink-0">
                <span className="text-[10px] font-semibold uppercase leading-none">
                  {(() => { try { return format(parseISO(day.date), 'MMM'); } catch { return ''; } })()}
                </span>
                <span className="text-base font-bold leading-none">
                  {(() => { try { return format(parseISO(day.date), 'd'); } catch { return ''; } })()}
                </span>
              </div>
              <div>
                <p className="font-display font-bold text-golf-gray text-sm">{dateLabel}</p>
                <p className="text-xs text-gray-400">{day.events.length} event{day.events.length !== 1 ? 's' : ''}</p>
              </div>
            </div>

            <div className="flex flex-col gap-2 ml-5 pl-5 border-l-2 border-golf-orange/20">
              {day.events.map((event) => (
                <div key={event.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
                  <p className="font-semibold text-golf-gray text-sm mb-1">{event.title}</p>
                  {event.description && (
                    <p className="text-xs text-gray-500 mb-2 leading-relaxed">{event.description}</p>
                  )}
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {event.time && (
                      <span className="flex items-center gap-1 text-xs text-golf-orange font-medium">
                        <ClockIcon className="w-3.5 h-3.5" />
                        {event.time}
                      </span>
                    )}
                    {event.location && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPinIcon className="w-3.5 h-3.5" />
                        {event.location}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
